export const normalizeSupplierApiOption = (raw) => {
  const option = raw || {};
  return { ...option, supplierId: option.supplierId ?? option.Id ?? option.id, name: option.name ?? option.Name ?? "Unnamed", capabilities: option.capabilities || {} };
};

export const supplierOptionSupportsCatalogue = (option) => {
  const capabilities = option?.capabilities || {};
  return option?.enabled === true && option?.connectionStatus === "connected" && capabilities.catalogueImport === true;
};

export const supplierOptionsForImportSource = (importSource, generalSuppliers, supplierApiOptions) =>
  importSource === "supplier_api" ? (supplierApiOptions || []).filter(supplierOptionSupportsCatalogue) : generalSuppliers;

export const supplierApiOptionsFromResponse = (response) =>
  (response?.data?.suppliers || response?.suppliers || []).map(normalizeSupplierApiOption).filter(supplierOptionSupportsCatalogue);

const PRODUCT_DATA_IMPORT_LIMITS = [1, 2, 5, 10, 25, 50, 100, 200];

export const productDataImportLimits = (max = 25) => {
  const cap = Number(max || 25);
  return PRODUCT_DATA_IMPORT_LIMITS.filter(limit => limit <= (cap === 25 ? 200 : cap));
};

export const supplierApiProductLimit = (importSource, value) => {
  if (importSource !== "supplier_api") return value;
  const parsed = Number(value);
  return PRODUCT_DATA_IMPORT_LIMITS.includes(parsed) ? parsed : 25;
};

export const appendCatalogueImportLimits = (form, { importSource, rowLimit, groupLimit, catalogueSince }) => {
  if (importSource === "file" && Number(rowLimit) > 0) form.append("limitRows", String(rowLimit));
  const submittedGroupLimit = supplierApiProductLimit(importSource, groupLimit);
  if (importSource === "supplier_api" || Number(submittedGroupLimit) > 0) {
    form.append("limitGroups", String(submittedGroupLimit));
  }
  if (importSource === "supplier_api" && catalogueSince) form.append("catalogueSince", String(catalogueSince));
};

export const buildSupplierApiSelectorModel = ({ importSource, suppliers = [], supplierApiOptions = [], uploading = false }) => ({
  disabled: Boolean(uploading),
  options: supplierOptionsForImportSource(importSource, suppliers, supplierApiOptions),
});

export const findSupplierApiOption = (options, value) =>
  (options || []).find(option => String(option.supplierId) === String(value)) || null;

export const buildSupplierApiMenuItems = ({ importSource, suppliers = [], supplierApiOptions = [], uploading = false }) =>
  buildSupplierApiSelectorModel({ importSource, suppliers, supplierApiOptions, uploading }).options.map(option => ({
    key: option.supplierId || option.Id,
    value: importSource === "supplier_api" ? option.supplierId : option.Name,
    label: option.capabilities?.lookupOnly ? `${option.name || option.Name} (lookup only)` : (option.name || option.Name),
    ...(option.capabilities?.lookupOnly === true ? { disabled: true } : {}),
  }));

export const resolveSupplierApiSelection = (options, value) => {
  const option = findSupplierApiOption(options, value);
  if (!option || !supplierOptionSupportsCatalogue(option)) return { option: null, connection: null };
  return { option, connection: { ...option, connected: true, status: "connected" } };
};

export const supplierApiSupplierPayload = option => option ? ({
  Id: option.supplierId,
  Name: option.name,
  ...(option.provider ? { provider: option.provider } : {}),
  ...(option.sourceType ? { sourceType: option.sourceType } : {}),
}) : null;

export const buildSupplierApiActionModel = (connection) => ({
  label: connection?.capabilities?.lookupOnly ? "Product lookup only" : connection?.capabilities?.catalogueImport ? "Import" : connection?.capabilities?.cataloguePreview ? "Preview Product Data" : "Import",
  submitMode: connection?.capabilities?.lookupOnly ? null : connection?.capabilities?.catalogueImport ? "import" : connection?.capabilities?.cataloguePreview ? "preview" : "import",
  ...(connection?.capabilities?.lookupOnly === true ? { disabled: true } : {}),
});

export const supplierApiConnectionLabel = (connection) => {
  if (connection?.status === "testing") return "Testingâ€¦";
  if (connection?.connected) return "â— Connected";
  if (connection?.capabilities?.catalogueImport === false) return "API pending";
  if (connection?.connectionStatus === "not_configured" || connection?.status === "not_configured") return "Not configured";
  if (connection?.connected === false) return "Connection failed";
  return "Select a configured Supplier API supplier";
};

export const supplierApiAvailabilityMessage = ({ importSource, loading, options = [] } = {}) =>
  importSource === "supplier_api" && !loading && options.length === 0
    ? "No active Supplier API import connection. Existing imported catalogue products may still appear in Products, but do not provide an active import connection."
    : null;

export const buildPreviewResultModel = (result) => {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  return { isPreviewOnly: result?.previewOnly === true, rows, count: rows.length, empty: rows.length === 0, showImportBatch: result?.previewOnly !== true };
};

const IMPORT_FAILURE_MESSAGES = Object.freeze({
  SUPPLIER_API_FETCH_TIMEOUT: 'The bounded catalogue fetch timed out. Enumeration support is still unknown for this attempt; no automatic retry was sent.',
  PRODUCT_DATA_FETCH_BUDGET_EXCEEDED: 'Catalogue fetch reached its time or call budget. Use a smaller batch or resume from a verified snapshot.',
  SUPPLIER_API_FETCH_INCOMPLETE: 'Catalogue fetching was incomplete. Import is disabled; no products from this attempt were imported.',
  PRODUCT_DATA_ENUMERATION_UNSUPPORTED: 'The supplier explicitly rejected catalogue enumeration. Product lookup can still be available.',
  PRODUCT_DATA_ENUMERATION_UNAVAILABLE: 'Enumeration is unavailable for this connection configuration. A lookup test does not prove catalogue enumeration.',
  PROMOSTANDARDS_REQUEST_FAILED: 'A transient transport failure prevented catalogue fetching. Enumeration capability was not changed.',
  PRODUCT_DATA_IMPORT_EMPTY: "The supplier returned no importable Product Data rows.",
  PROMOSTANDARDS_RESPONSE_TOO_LARGE: "The supplier DateModified response exceeded the safe processing limit. Choose a newer Catalogue changes since date and try again, or use a registered catalogue preview; first-N is applied only after enumeration is fetched.",
  PROMOSTANDARDS_INVALID_RESPONSE: "The supplier returned a Product Data response shape this importer does not support.",
  PROMOSTANDARDS_REQUEST_TIMEOUT: "The supplier request timed out before catalogue data was returned.",
});
const safeDiagnosticToken = (value, pattern, maxLength = 160) =>
  typeof value === "string" && value.length <= maxLength && pattern.test(value) ? value : null;

export const buildImportFailureDetails = ({ status, diagnostics, issueSamples = [] } = {}) => {
  if (status !== "failed" || !diagnostics || typeof diagnostics !== "object" || Array.isArray(diagnostics)) return null;
  const errorCode = safeDiagnosticToken(diagnostics.errorCode, /^[A-Z][A-Z0-9_]*$/, 128);
  const usefulIssues = Array.isArray(issueSamples) && issueSamples.some(issue => issue && typeof issue === "object" &&
    (typeof (issue.code || issue.Code) === "string" || typeof (issue.message || issue.Message) === "string"));
  if (!errorCode || usefulIssues) return null;
  const failureStage = safeDiagnosticToken(diagnostics.failureStage, /^[a-z][a-z0-9_-]*$/, 80);
  const provider = safeDiagnosticToken(diagnostics.provider, /^[A-Za-z0-9_.:-]+$/);
  const sourceType = safeDiagnosticToken(diagnostics.sourceType, /^[A-Za-z0-9_-]+$/, 80);
  return {
    errorCode,
    friendlyMessage: IMPORT_FAILURE_MESSAGES[errorCode] || null,
    ...(safeCatalogueFetchDetails(diagnostics.catalogueFetch) ? { catalogueFetch: safeCatalogueFetchDetails(diagnostics.catalogueFetch) } : {}),
    ...(failureStage ? { failureStage } : {}),
    ...(provider ? { provider } : {}),
    ...(sourceType ? { sourceType } : {}),
    ...(typeof diagnostics.sourceComplete === "boolean" ? { sourceComplete: diagnostics.sourceComplete } : {}),
  };
};

export const snapshotDryRunValues = values => {
  const { continueFromBatchId: _continueFromBatchId, ...snapshot } = values || {};
  return {
    ...snapshot,
    supplierObj: snapshot?.supplierObj ? { ...snapshot.supplierObj } : snapshot?.supplierObj,
  };
};

export const safeCatalogueFetchDetails = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = {};
  if (['GetProductSellable','GetProductDateModified','GetProduct','GetMediaContent','GetConfigurationAndPricing'].includes(value.operation)) result.operation = value.operation;
  if (['running','complete','bounded_batch_complete','partial','timed_out','budget_exhausted','unsupported','transport_failed','cancelled'].includes(value.status)) result.status = value.status;
  for (const key of ['elapsedMs','timeoutMs','operationElapsedMs','remainingMs','budgetMs','calls','maxCalls','completedCalls','completedProducts','enumeratedCount']) if (Number.isSafeInteger(value[key]) && value[key] >= 0 && value[key] <= 3600000) result[key] = value[key];
  return result.status ? result : null;
};
export const catalogueFetchMessage = (diagnostics = {}, status, dryRun) => {
  if (diagnostics.outcome === 'no_results' && diagnostics.fetchSucceeded === true && diagnostics.batchComplete === true) return 'No products returned for this locale.';
  const fetch = safeCatalogueFetchDetails(diagnostics.catalogueFetch);
  if (fetch?.status === 'timed_out') return 'Bounded catalogue fetch timed out. Remote completion is unknown; import is disabled.';
  if (fetch?.status === 'unsupported') return 'Catalogue enumeration was explicitly unsupported; this is separate from product lookup.';
  if (fetch?.status === 'transport_failed') return 'Transport or response validation failed. Enumeration capability was not changed.';
  if (['partial','budget_exhausted','cancelled'].includes(fetch?.status)) return 'Catalogue fetch is partial or incomplete. Import is disabled; retry explicitly from verified state.';
  if (status === 'completed' && dryRun && fetch?.status === 'bounded_batch_complete') return 'Bounded batch dry run passed. This batch can be imported; the full source is not complete.';
  if (status === 'completed' && dryRun && fetch?.status === 'complete') return 'Complete catalogue dry run passed.';
  return null;
};
const incompleteCatalogueFetch = result => {
  const diagnostics = result?.batch?.Diagnostics || result?.diagnostics || {};
  return ['running','partial','timed_out','budget_exhausted','unsupported','transport_failed','cancelled'].includes(safeCatalogueFetchDetails(diagnostics.catalogueFetch)?.status);
};
const isFullCatalogueResult = result => result?.catalogueMode === 'full_catalogue' || (result?.batch?.Diagnostics || result?.diagnostics)?.fullCatalogueJob;
export const dryRunImportActionState = ({ result, polling, batchStatus, uploading, hasSavedValues = true }) => ({
  visible: result?.dryRun === true && !isFullCatalogueResult(result),
  disabled: Boolean(isFullCatalogueResult(result) || !hasSavedValues || incompleteCatalogueFetch(result) ||
    (result?.batch?.Diagnostics || result?.diagnostics)?.outcome === 'no_results' ||
    (result?.batch?.Diagnostics || result?.diagnostics)?.validatedRowCount === 0 ||
    polling || batchStatus !== "completed" || result?.canImport === false || uploading || result?.dryRun !== true),
});

// Remember destination choices separately so switching sources preserves file defaults.
export const catalogueImportDestinations = [
  { value: "instance_custom", label: "Instance Custom Products" },
  { value: "promoworld", label: "PromoWorld Global Catalogue" },
];

export const destinationForImportSource = (source, selections = {}, available = catalogueImportDestinations.map(item => item.value)) => {
  if (available.includes(selections[source])) return selections[source];
  if (source === "supplier_api" && available.includes("promoworld")) return "promoworld";
  return available.includes("instance_custom") ? "instance_custom" : available[0] || "";
};

export const buildImportDisplayContext = (result, submitted = {}) => {
  const batch = result?.batch || result?.importBatch || {};
  const destination = result?.destination || batch.Diagnostics?.destination || result?.diagnostics?.destination || submitted?.destination || "instance_custom";
  const supplierName = [batch.SupplierName, submitted?.supplierName, submitted?.supplierObj?.Name]
    .find(value => typeof value === "string" && value.trim());
  return {
    supplierName: supplierName?.trim() || null,
    destination,
    destinationLabel: catalogueImportDestinations.find(item => item.value === destination)?.label || destination,
  };
};

export const supplierApiEstimateMessage = (estimate, loading = false) => {
  if (loading) return "Catalogue estimate: loading...";
  if (!estimate) return null;
  if (estimate.available !== true) return `Catalogue estimate: unavailable${estimate.warning ? ` · ${estimate.warning}` : ""}`;
  const discovered = Number.isSafeInteger(estimate.totalDiscoveredProductGroups) && estimate.totalDiscoveredProductGroups > 0
    ? estimate.totalDiscoveredProductGroups : null;
  if (discovered == null) return `Catalogue estimate: unavailable${estimate.warning ? ` · ${estimate.warning}` : ""}`;
  return `Catalogue estimate: ${estimate.importedProductCardCount ?? "?"} imported / ${discovered} discovered · batch size ${estimate.safeBatchSize ?? "?"}`;
};

export const canContinueProductDataImport = ({ result, submitted, polling = false, uploading = false } = {}) => {
  const batch = result?.batch || result?.importBatch || {};
  const diagnostics = { ...result?.diagnostics, ...batch.Diagnostics };
  const run = diagnostics.productDataImportRun || {};
  const sourceType = diagnostics.sourceType || run.sourceType || submitted?.sourceType;
  const isProductData = sourceType === "supplier_service_product_data" || run.instanceSource === "supplier_api_product_data";
  const dryRun = result?.dryRun ?? batch.DryRun ?? run.dryRun ?? submitted?.dryRun;
  const sourceComplete = run.sourceComplete ?? diagnostics.sourceComplete ?? result?.sourceComplete;
  // Bounded imports can report sourceComplete=false for reconciliation even at the end.
  const cursor = [run.lastProcessedProductId, diagnostics.resumeCursor?.lastProcessedProductId]
    .find(value => typeof value === "string" && value.trim() && value.length <= 160 && !/[\u0000-\u001f\u007f<>]/.test(value));
  // Honor a validated run's final signal; older batches may only have the raw cursor.
  const hasRunCursor = cursor && cursor === run.lastProcessedProductId;
  const hasMore = run.canContinue === true || (sourceComplete === false && (run.canContinue !== false || !hasRunCursor));
  const exhausted = Number.isSafeInteger(run.totalDiscovered) && Number.isSafeInteger(run.processedCount)
    && run.processedCount >= run.totalDiscovered;
  return Boolean(isProductData && cursor && submitted?.importSource !== "file" && (batch.id || result?.importBatchId) && batch.Status === "completed"
    && dryRun === false && !result?.previewOnly && !polling && !uploading
    && sourceComplete !== true && !exhausted && hasMore);
};


export const supportsProductDataAutoContinue = (source, connection) => source === "supplier_api"
  && connection?.sourceType === "supplier_service_product_data" && connection?.capabilities?.catalogueImport === true;

export const productDataAutoContinueDecision = ({ result, submitted }) => {
  const batch = result?.batch || result?.importBatch || {};
  if (result?.dryRun || batch.DryRun || submitted?.dryRun) return { action: "stop", message: "" };
  if (batch.Status === "failed" || Number(batch.Errors) > 0 || Number(result?.summary?.errors) > 0)
    return { action: "stop", message: "Auto-continue stopped because this batch reported errors." };
  if (batch.Status === "cancelled" || batch.Status === "cancel_requested")
    return { action: "stop", message: "Auto-continue stopped because the import was cancelled." };
  if (batch.Status !== "completed") return { action: "wait" };
  if (canContinueProductDataImport({ result, submitted })) return { action: "next" };
  const diagnostics = { ...result?.diagnostics, ...batch.Diagnostics };
  const run = diagnostics.productDataImportRun || {};
  const complete = (run.sourceComplete ?? diagnostics.sourceComplete ?? result?.sourceComplete) === true
    || (Number.isSafeInteger(run.totalDiscovered) && Number.isSafeInteger(run.processedCount) && run.processedCount >= run.totalDiscovered)
    || (run.canContinue === false && Boolean(run.lastProcessedProductId));
  return { action: "stop", message: complete ? "Auto-continue finished: catalogue complete."
    : "Auto-continue stopped: no resumable cursor or continuation is unavailable." };
};
